import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { logger } from '@/utils/logger'
import { useWebSocket } from '@/contexts/websocket-context'
import type { BatchProgressEventData, BatchStatusEventData } from '@/types/websocket'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from '@/components/common/ui/use-toast'
import { useTranslation } from 'react-i18next'
import { getGenerationJobStatus } from '@/api/requests/prompt'

export interface PromptGenerationJob {
  batchId: string
  spaceId: string
  serviceId: string
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED'
  startedAt: Date
  completedAt?: Date
  totalCount?: number
  completedCount?: number
  failedCount?: number
  // 🔧 Batch type identification for proper separation
  batchType: 'prompt-generation'
}

interface PromptGenerationContextValue {
  jobs: Map<string, PromptGenerationJob>
  addJob: (job: Omit<PromptGenerationJob, 'startedAt'>) => void
  updateJob: (batchId: string, updates: Partial<PromptGenerationJob>) => void
  removeJob: (batchId: string) => void
  getActiveJobs: () => PromptGenerationJob[]
}

const PromptGenerationContext = createContext<PromptGenerationContextValue | null>(null)

export const usePromptGenerationContext = () => {
  const context = useContext(PromptGenerationContext)
  if (!context) {
    throw new Error('usePromptGenerationContext must be used within PromptGenerationProvider')
  }
  return context
}

interface PromptGenerationProviderProps {
  children: React.ReactNode
}

export const PromptGenerationProvider: React.FC<PromptGenerationProviderProps> = ({ children }) => {
  const [jobs, setJobs] = useState<Map<string, PromptGenerationJob>>(new Map())
  const webSocketContext = useWebSocket()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  // Clean up completed/failed jobs after delay
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now()
      setJobs(currentJobs => {
        const newJobs = new Map(currentJobs)
        let hasChanges = false

        for (const [batchId, job] of newJobs.entries()) {
          if (job.status === 'COMPLETED' || job.status === 'FAILED' || job.status === 'CANCELLED') {
            if (!job.completedAt) {
              job.completedAt = new Date(now)
              logger.debug('[PromptGenerationContext] Job completion timestamp set', {
                batchId,
                status: job.status,
                completedAt: job.completedAt,
              })
            } else if (now - job.completedAt.getTime() > 3000) {
              // 3 seconds after completion
              logger.debug('[PromptGenerationContext] Cleaning up completed job', {
                batchId,
                status: job.status,
                completedTime: job.completedAt,
                timeSinceCompletion: now - job.completedAt.getTime(),
              })
              newJobs.delete(batchId)
              hasChanges = true
            }
          }
        }

        return hasChanges ? newJobs : currentJobs
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  // Subscribe to WebSocket events for prompt generation batches
  useEffect(() => {
    if (!webSocketContext) return

    const { onBatchProgress, onBatchCompleted, onBatchFailed } = webSocketContext

    const unsubscribeProgress = onBatchProgress((data: BatchProgressEventData) => {
      // Only process batches that exist in our jobs map (prompt generation jobs)
      setJobs(currentJobs => {
        const existingJob = currentJobs.get(data.batchId)
        if (!existingJob || existingJob.batchType !== 'prompt-generation') {
          logger.debug(
            '[PromptGenerationContext] Ignoring progress event for non-prompt-generation batch',
            {
              batchId: data.batchId,
              batchType: existingJob?.batchType || 'unknown',
              hasJob: !!existingJob,
            }
          )
          return currentJobs
        }

        const newJobs = new Map(currentJobs)
        newJobs.set(data.batchId, {
          ...existingJob,
          status: 'RUNNING', // Always keep as RUNNING during progress updates
          totalCount: existingJob.totalCount ?? data.totalCount,
          completedCount: data.completedCount,
          failedCount: data.failedCount,
        })
        return newJobs
      })
    })

    const unsubscribeCompleted = onBatchCompleted(async (data: BatchStatusEventData) => {
      let jobSnapshot: PromptGenerationJob | undefined

      setJobs(currentJobs => {
        const existingJob = currentJobs.get(data.batchId)
        if (!existingJob || existingJob.batchType !== 'prompt-generation') {
          console.debug(
            '[PromptGenerationContext] Ignoring completed event for non-prompt-generation batch:',
            {
              batchId: data.batchId,
              batchType: existingJob?.batchType || 'unknown',
              hasJob: !!existingJob,
            }
          )
          return currentJobs
        }

        const updatedJob: PromptGenerationJob = {
          ...existingJob,
          status: 'COMPLETED',
          completedAt: new Date(),
        }

        jobSnapshot = updatedJob

        const newJobs = new Map(currentJobs)
        newJobs.set(data.batchId, updatedJob)
        return newJobs
      })

      if (!jobSnapshot) {
        return
      }

      logger.info('[PromptGenerationContext] Prompt generation completed - refreshing data', {
        batchId: data.batchId,
        spaceId: jobSnapshot.spaceId,
        serviceId: jobSnapshot.serviceId,
      })

      let generatedPromptCount = jobSnapshot.completedCount ?? jobSnapshot.totalCount ?? 0

      try {
        const statusResponse = await getGenerationJobStatus(jobSnapshot.spaceId, data.batchId)
        const remoteProgress = statusResponse?.job?.progress
        const remoteResults = statusResponse?.job?.results

        if (typeof remoteProgress?.generatedPrompts === 'number') {
          generatedPromptCount = remoteProgress.generatedPrompts
        } else if (typeof remoteResults?.newQuestionCount === 'number') {
          generatedPromptCount = remoteResults.newQuestionCount
        }

        const totalExpected =
          typeof remoteProgress?.totalExpectedPrompts === 'number'
            ? remoteProgress.totalExpectedPrompts
            : jobSnapshot.totalCount

        setJobs(currentJobs => {
          const existingJob = currentJobs.get(data.batchId)
          if (!existingJob) {
            return currentJobs
          }

          const newJobs = new Map(currentJobs)
          newJobs.set(data.batchId, {
            ...existingJob,
            completedCount: generatedPromptCount,
            totalCount: totalExpected,
          })
          return newJobs
        })
      } catch (error) {
        logger.warn('[PromptGenerationContext] Failed to fetch completed job status', {
          batchId: data.batchId,
          error,
        })
      }

      // Invalidate related queries after completion
      queryClient.invalidateQueries({
        queryKey: ['prompts', jobSnapshot.spaceId, jobSnapshot.serviceId],
      })

      queryClient.invalidateQueries({
        queryKey: ['keywordGroups', jobSnapshot.spaceId, jobSnapshot.serviceId],
      })

      queryClient.invalidateQueries({
        queryKey: ['promptStats', jobSnapshot.spaceId, jobSnapshot.serviceId],
      })

      toast({
        title: t('promptManagement.generation.completedTitle'),
        description: t('promptManagement.generation.completedDesc', {
          count: generatedPromptCount,
        }),
        variant: 'default',
        className: 'mb-24',
      })
    })

    const unsubscribeFailed = onBatchFailed((data: BatchStatusEventData) => {
      setJobs(currentJobs => {
        const existingJob = currentJobs.get(data.batchId)
        if (!existingJob || existingJob.batchType !== 'prompt-generation') {
          console.debug(
            '[PromptGenerationContext] Ignoring failed event for non-prompt-generation batch:',
            {
              batchId: data.batchId,
              batchType: existingJob?.batchType || 'unknown',
              hasJob: !!existingJob,
            }
          )
          return currentJobs
        }

        const newJobs = new Map(currentJobs)
        newJobs.set(data.batchId, {
          ...existingJob,
          status: 'FAILED',
          completedAt: new Date(),
        })

        // 🚨 Show error notification for failed generation
        logger.error('[PromptGenerationContext] Prompt generation failed', {
          batchId: data.batchId,
          spaceId: existingJob.spaceId,
          serviceId: existingJob.serviceId,
          error: data.error,
        })

        // Show error notification
        toast({
          title: t('promptManagement.generation.failedTitle'),
          description: data.error || t('promptManagement.generation.failedDesc'),
          variant: 'destructive',
          className: 'mb-24',
        })

        return newJobs
      })
    })

    return () => {
      unsubscribeProgress()
      unsubscribeCompleted()
      unsubscribeFailed()
    }
  }, [webSocketContext])

  // Context methods
  const addJob = useCallback((jobData: Omit<PromptGenerationJob, 'startedAt'>) => {
    console.log('[PromptGenerationContext] 📥 addJob called with:', jobData)

    setJobs(currentJobs => {
      const newJobs = new Map(currentJobs)
      const job = {
        ...jobData,
        startedAt: new Date(),
      }
      newJobs.set(jobData.batchId, job)

      console.log('[PromptGenerationContext] ✅ Job added successfully:', {
        batchId: jobData.batchId,
        status: job.status,
        batchType: job.batchType,
        totalJobs: newJobs.size,
        allJobs: Array.from(newJobs.values()).map(j => ({
          id: j.batchId,
          status: j.status,
          type: j.batchType,
          startedAt: j.startedAt,
        })),
        timestamp: new Date().toISOString(),
      })

      return newJobs
    })
  }, [])

  const updateJob = useCallback((batchId: string, updates: Partial<PromptGenerationJob>) => {
    setJobs(currentJobs => {
      const existingJob = currentJobs.get(batchId)
      if (!existingJob) return currentJobs

      const newJobs = new Map(currentJobs)
      newJobs.set(batchId, { ...existingJob, ...updates })
      return newJobs
    })
  }, [])

  const removeJob = useCallback((batchId: string) => {
    setJobs(currentJobs => {
      const newJobs = new Map(currentJobs)
      newJobs.delete(batchId)
      console.log('[PromptGenerationContext] Job removed:', batchId)
      return newJobs
    })
  }, [])

  const getActiveJobs = useCallback((): PromptGenerationJob[] => {
    const allJobs = Array.from(jobs.values())
    const activeJobs = allJobs.filter(job => job.status === 'PENDING' || job.status === 'RUNNING')

    console.log('[PromptGenerationContext] 🔍 getActiveJobs called:', {
      totalJobs: allJobs.length,
      activeJobs: activeJobs.length,
      allJobDetails: allJobs.map(job => ({
        id: job.batchId,
        status: job.status,
        batchType: job.batchType,
        startedAt: job.startedAt,
      })),
      activeJobIds: activeJobs.map(job => job.batchId),
      timestamp: new Date().toISOString(),
    })

    return activeJobs
  }, [jobs])

  const value: PromptGenerationContextValue = {
    jobs,
    addJob,
    updateJob,
    removeJob,
    getActiveJobs,
  }

  return (
    <PromptGenerationContext.Provider value={value}>{children}</PromptGenerationContext.Provider>
  )
}
